import React, { useCallback, useEffect, useState, useRef } from "react";
import CommonLayout from "../../../Components/CommonLayout/CommonLayout.jsx";
import BasicTable from "../../../Components/CommonTable/BasicTable/BasicTable.jsx";
import earningApis from "../../../App/api/earning.apis";
import { useFetchAll } from "../../../custom/FetchApiHook.jsx";
import { formattedAmount } from "../../../utils/helpers/Functions.helper";
import { useNavigate } from "react-router";

const EarningStore = ({ meta }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    sortOrder: "desc",
    filter: {},
  });
  const [selectedData, setSelectedData] = useState(null);
  const debounceTimeout = useRef(null);

  // API: POST order/get-all-vendor-stats
  // Response: { data: { vendors[], page, limit, totalPages, totalCount } }
  const { value, fetchAllData } = useFetchAll(() =>
    earningApis.getAllVendorStats(pagination, search)
  );

  const vendors = value?.data?.vendors || [];

  const columns = [
    {
      field: "vendorName",
      header: "Store Name",
      accessor: (row) => row.vendor?.first_name || row.vendor?.email || "N/A",
    },
    {
      field: "email",
      header: "Email",
      accessor: (row) => row.vendor?.email || "-",
    },
    {
      field: "phone_number",
      header: "Phone",
      accessor: (row) => row.vendor?.phone_number || "-",
    },
    {
      field: "user_role",
      header: "Role",
      accessor: (row) => row.vendor?.user_role?.name || "-",
    },
    {
      field: "totalOrders",
      header: "Total Orders",
      accessor: (row) => row.totalOrders ?? 0,
    },
    {
      field: "deliveredOrders",
      header: "Delivered",
      accessor: (row) => (
        <span className="text-green-600 font-semibold">
          {row.deliveredOrders ?? 0}
        </span>
      ),
    },
    {
      field: "pendingToDeliveryOrders",
      header: "Pending Delivery",
      accessor: (row) => (
        <span className="text-orange-500 font-semibold">
          {row.pendingToDeliveryOrders ?? 0}
        </span>
      ),
    },
    {
      field: "totalVendorEarning",
      header: "Total Earning",
      accessor: (row) => (
        <span className="text-blue-600 font-semibold">
          {formattedAmount(row.totalVendorEarning || 0)}
        </span>
      ),
    },
  ];

  const searchBy = {
    filterType: "Search By",
    inputName: "search_by",
    options: [
      { name: "Store", code: "store" },
      { name: "Email", code: "email" },
    ],
  };

  const handlePagination = (data) => {
    setPagination((prev) => ({
      ...prev,
      page: data.page ? data.page : prev.page,
      limit: data.limit ? data.limit : prev.limit,
      sortOrder: data.sortOrder ? data.sortOrder : prev.sortOrder,
    }));
  };

  const onPaginatorClick = (e) => {
    if (e) {
      handlePagination({ page: +e.page + 1, limit: e.rows });
    }
  };

  const handleSearch = useCallback((val) => {
    setSearch(val);
  }, []);

  const debouncedSearch = useCallback(
    (val) => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => handleSearch(val), 400);
    },
    [handleSearch]
  );

  const handleClearFilter = () => {
    setSearch("");
    setPagination({ page: 1, limit: 10, sortOrder: "desc", filter: {} });
  };

  // On row click → navigate to that vendor's product earnings detail page
  const handleRowClick = (row) => {
    const vendorId = row.vendor?._id || row._id;
    if (vendorId) {
      navigate(`/app/earnings/store/${vendorId}`);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [pagination, search]);

  return (
    <div className="main-content">
      <CommonLayout />
      <BasicTable
        columns={columns}
        data={vendors}
        onPaginatorClick={onPaginatorClick}
        paginatorData={{
          filters: null,
          first: 0,
          multiSortMeta: [],
          page: value?.data?.page || 1,
          rows: value?.data?.limit || pagination.limit,
          sortField: null,
          sortOrder: null,
          totalPages: value?.data?.totalPages || 1,
          totalCount: value?.data?.totalCount || 0,
        }}
        SearchBy={searchBy}
        handleSearch={debouncedSearch}
        handleClearFilter={handleClearFilter}
        WeekRange={false}
        MonthRange={false}
        selectedData={selectedData}
        handleRowClick={handleRowClick}
      />
    </div>
  );
};

export default EarningStore;